import { ComponentType, useMemo, useState } from "react";
import { MagnifyingGlassIcon, NoSymbolIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";

export interface IconOption {
  // The value stored in the form, and sent to the API as-is.
  id: string;
  name: string;
  // Omitted for values that have no icon to render, e.g. one saved elsewhere.
  Icon?: ComponentType<{ className?: string }>;
}

interface IconSelectInputProps {
  label: string;
  register: any;
  registerName: string;
  setValue: any;
  watch: any;
  options: IconOption[];
  cols?: string;
  allowClear?: boolean;
  // Overrides how the "no icon selected" tile looks, e.g. to show what gets rendered
  // in its place elsewhere.
  clearOption?: IconOption;
  required?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  helpText?: string;
}

export default function IconSelectInput({
  label,
  register,
  registerName,
  setValue,
  watch,
  options,
  cols = "sm:col-span-1",
  allowClear = true,
  clearOption,
  required = false,
  disabled = false,
  searchable = true,
  helpText,
}: IconSelectInputProps) {
  const { t } = useTranslation("common");
  const [query, setQuery] = useState("");
  const selectValue = watch(registerName);
  const selectedOption = options.find((x) => x.id === selectValue);
  const summaryOption = selectValue
    ? selectedOption
    : clearOption ?? { id: "", name: t("None") };

  const filteredOptions = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return options;
    return options.filter(
      (x) => x.name.toLowerCase().includes(search) || x.id.toLowerCase().includes(search)
    );
  }, [options, query]);

  const select = (id: string) => {
    if (disabled) return;
    setValue(registerName, id, { shouldDirty: true });
  };

  const tileClasses = (isSelected: boolean) =>
    classNames(
      isSelected
        ? "border-primary-500 ring-1 ring-primary-500 bg-primary-50 text-primary-700"
        : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50",
      disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      "inline-flex h-10 w-10 items-center justify-center rounded border"
    );

  return (
    <div className={cols}>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-gray-500">
          {label}{required && <span className="text-primary-500">*</span>}
        </label>
        <span className="flex items-center gap-2 text-sm text-gray-500">
          {summaryOption?.Icon && <summaryOption.Icon className="h-5 w-5 text-gray-700" />}
          {summaryOption?.name ?? selectValue}
        </span>
      </div>

      <input type="hidden" {...register(registerName)} />

      {searchable && (
        <div className="relative mt-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={disabled}
            placeholder={t("Search icons")}
            className="w-full border-1 border-gray-300 py-2 pl-8 pr-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      )}

      <div className="mt-1 max-h-56 overflow-y-auto border border-gray-300 p-2">
        {filteredOptions.length === 0 && (
          <p className="p-2 text-sm text-gray-500">{t('No icons match "{{query}}".', { query })}</p>
        )}
        <div className="grid grid-cols-6 gap-2 sm:grid-cols-10">
          {allowClear && (
            <button
              type="button"
              title={clearOption?.name ?? t("None")}
              aria-label={clearOption?.name ?? t("None")}
              aria-pressed={!selectValue}
              disabled={disabled}
              className={tileClasses(!selectValue)}
              onClick={() => select("")}
            >
              {clearOption?.Icon ? (
                <clearOption.Icon className="h-5 w-5" />
              ) : (
                <NoSymbolIcon className="h-5 w-5" />
              )}
            </button>
          )}
          {filteredOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              title={`${option.name} (${option.id})`}
              aria-label={option.name}
              aria-pressed={selectValue === option.id}
              disabled={disabled}
              className={tileClasses(selectValue === option.id)}
              onClick={() => select(option.id)}
            >
              {option.Icon ? (
                <option.Icon className="h-5 w-5" />
              ) : (
                <span className="w-full truncate px-1 text-[10px] leading-none">{option.id}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
    </div>
  );
}
